import { useSyncExternalStoreWithSelector } from 'use-sync-external-store/shim/with-selector';
import type { CleanupFunction } from '../brands/cleanupFunction';

const call = (fn: () => void) => {
  fn();
};
const identity = <T>(x: T) => x;

/**
 * Creates a simple store with a single state object and a set of listeners.
 * The store can be subscribed to, and listeners will be called whenever the state changes.
 * The store can be updated by calling `setState` with a new state or a function that takes the current state and returns a new state.
 * The store can be subscribed to with a selector function that will be called whenever the state changes.
 * Inspired by Zustand, but with an even simpler API.
 * Intended for cases where React context is the optimal candidate, but slightly more ergonomics is desired.
 * @param initialState The initial state of the store
 * @param isEqual A function that compares two states and returns true if they are equal
 * @param onSetState A function that is called whenever the state changes. Just like subscribing, but the callback gives access to the both the new and the previous state. Intended for sync with local storage or similar.
 * @returns An object with methods to interact with the store
 */
export function makeStore<T extends object | string | number | null>(
  initialState: T,
  isEqual: (a: T, b: T) => boolean = Object.is,
  onSetState?: (newState: T, prevState: T) => void
) {
  const listeners = new Set<() => void>();

  let state = initialState;

  const getState = (): T => state;

  const setState = (newState: T | ((state: T) => T)): void => {
    if (typeof newState === 'function') {
      newState = newState(state) as T;
    }
    if (!isEqual(state, newState)) {
      onSetState?.(newState, state);
      state = newState;
      listeners.forEach(call);
    }
  };

  const subscribe = (listener: () => void): CleanupFunction => {
    listeners.add(listener);
    return (() => {
      listeners.delete(listener);
    }) as CleanupFunction;
  };

  const useStore: {
    (): T;
    /**
     *
     * @param derive A function that takes the current state and returns a derived value.
     * Component only re-renders when the derived value changes.
     * Note that a returned new object always means a new reference.
     * @param isEqual A function that compares two derived values and returns true if they are equal.
     * This prevents unnecessary re-renders (as well as infinite loops) when the derived value is a new object.
     */
    <U>(derive: (state: T) => U, isEqual?: (a: U, b: U) => boolean): U;
  } = <U>(derive?: (state: T) => U, isEqual?: (a: U, b: U) => boolean) => {
    return useSyncExternalStoreWithSelector<T, U>(
      subscribe,
      getState,
      undefined,
      derive ?? (identity as (state: T) => U),
      isEqual
    );
  };

  const getInitialState = () => initialState;

  return {
    getState,
    setState,
    subscribe,
    useStore,
    getInitialState,
  };
}

type _Merge<
  T,
  K extends PropertyKey = T extends unknown ? keyof T : never,
> = T extends unknown ? T & Partial<Record<Exclude<K, keyof T>, never>> : never;

type Merge<T> = { [K in keyof _Merge<T>]: _Merge<T>[K] };

export type SelectorMap<T extends object> = {
  [K in keyof Merge<T>]: Merge<T>[K] extends undefined
    ? never
    : (state: Merge<T>) => Merge<T>[K];
};

export function makeSelectors<T extends object>(obj: T) {
  return Object.fromEntries(
    (Object.keys(obj) as (keyof Merge<T>)[]).map(
      (key) => [key, (state: Merge<T>) => state[key]] as const
    )
  ) as SelectorMap<T>;
}
